import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { Check } from 'lucide-react';
import bgImage from '../img/ID_BKG.jpeg';
import "./Profile.css";

const Adminprofile = () => {
  const [userData, setUserData] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [mealDistribution, setMealDistribution] = useState([
    { id: 1, date: '2025-10-29', breakfast: true, lunch: true, hiTea: true, dinner: false },
    { id: 2, date: '2025-10-30', breakfast: true, lunch: false, hiTea: false, dinner: false },
    { id: 3, date: '2025-10-31', breakfast: false, lunch: false, hiTea: false, dinner: false },
    { id: 4, date: '2025-11-01', breakfast: false, lunch: false, hiTea: false, dinner: false },
    { id: 5, date: '2025-11-02', breakfast: false, lunch: false, hiTea: false, dinner: false }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrData, setQrData] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const idCardRef = useRef(null);

  // Get user photo URL - for PAGE DISPLAY
  const getUserPhotoUrl = (userData) => {
    if (!userData) {
      console.log('getUserPhotoUrl: No userData');
      return null;
    }

    console.log('=== CHECKING PHOTO FIELDS ===');
    console.log('User data keys:', Object.keys(userData));

    const photoFields = [
      'photoPath', 'PhotoPath',
      'photo', 'Photo',
      'photoUrl', 'PhotoUrl', 'PhotoURL',
      'image', 'Image',
      'imageUrl', 'ImageUrl', 'ImageURL',
      'profilePicture', 'ProfilePicture',
      'profilePhoto', 'ProfilePhoto',
      'avatar', 'Avatar',
      'picture', 'Picture'
    ];

    for (const field of photoFields) {
      if (userData[field] && userData[field].toString().trim()) {
        const photoValue = userData[field].toString().trim();
        console.log(`✓ Found ${field}:`, photoValue);

        if (photoValue.startsWith('http://') || photoValue.startsWith('https://')) {
          console.log('→ Using full URL:', photoValue);
          return photoValue;
        }

        if (photoValue.startsWith('data:image')) {
          console.log('→ Using base64');
          return photoValue;
        }

        // USE MEMBERS API BASE URL
        if (photoValue.startsWith('/')) {
          const baseUrl = "https://api.asterickx.com";
          const fullUrl = `${baseUrl}${photoValue}`;
          console.log('→ Constructed URL from relative path:', fullUrl);
          return fullUrl;
        }

        const baseUrl = "https://api.asterickx.com";
        const fullUrl = `${baseUrl}/uploads/${photoValue}`;
        console.log('→ Constructed from filename:', fullUrl);
        return fullUrl;
      }
    }

    console.log('✗ No photo field found in userData');
    console.log('Available fields:', Object.keys(userData).join(', '));
    return null;
  };

  // Get phone/mobile number
  const getUserPhone = (userData) => {
    if (!userData) return null;
    const phoneFields = ['phoneNumber', 'PhoneNumber', 'phone', 'Phone', 'mobile', 'Mobile', 'mobileNumber', 'MobileNumber', 'contactNumber'];
    for (const field of phoneFields) {
      if (userData[field] && userData[field].toString().trim()) {
        return userData[field].toString().trim();
      }
    }
    return null;
  };

  // Get state
  const getUserState = (userData) => {
    if (!userData) return null;
    const stateFields = ['state', 'State', 'stateName', 'StateName'];
    for (const field of stateFields) {
      if (userData[field] && userData[field].toString().trim()) {
        return userData[field].toString().trim();
      }
    }
    return null;
  };

  // Decode JWT
  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  // Download ID card as PDF - 70mm x 100mm dimensions
  const downloadIDCard = async () => {
    if (!idCardRef.current) {
      alert('ID card not found');
      return;
    }

    try {
      setDownloading(true);
      console.log('Downloading ID card as PDF (70mm x 100mm)...');

      // Get token for authorization
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token') ||
        localStorage.getItem('Token') ||
        (storedUser ? JSON.parse(storedUser).token : null);

      // STEP 1: Fetch base64 image from API
      let imageBase64 = null;

      // Get user ID from userDetails or userData state
      const currentUserData = userDetails || userData;
      const userId = currentUserData?.id ||
        currentUserData?.userId ||
        currentUserData?.memberId ||
        currentUserData?.ID;

      console.log('=== FETCHING BASE64 IMAGE ===');
      console.log('Current user data:', currentUserData);
      console.log('User ID:', userId);
      console.log('User ID type:', typeof userId);

      if (userId) {
        try {
          console.log('Fetching base64 image from API...');
          
          const myHeaders = new Headers();
          myHeaders.append("Authorization", `Bearer ${token}`);

          const requestOptions = {
            method: "GET",
            headers: myHeaders,
            redirect: "follow"
          };

          // AWAIT the fetch call properly
          const response = await fetch(
            `https://api.asterickx.com/api/Members/members/photo/${userId}`, 
            requestOptions
          );

          if (!response.ok) {
            throw new Error(`API returned status ${response.status}`);
          }

          const data = await response.json();
          console.log("✓ Received base64 from API");

          if (data.base64) {
            imageBase64 = data.base64;
            console.log("✓ Base64 stored successfully");
          } else {
            console.warn("⚠️ API response did not contain base64 field");
          }

        } catch (error) {
          console.error('❌ Error fetching base64 from API:', error);
          console.log('Continuing without base64 replacement...');
        }
      } else {
        console.warn('⚠️ No user ID found, cannot fetch base64 image');
      }

      // STEP 2: Replace image source with base64 if available
      const profileImage = idCardRef.current.querySelector('img[alt="Profile"]');
      let originalImageSrc = null;

      if (imageBase64 && profileImage) {
        originalImageSrc = profileImage.src;
        // Add data:image prefix if not present
        const base64WithPrefix = imageBase64.startsWith('data:image') 
          ? imageBase64 
          : `data:image/jpeg;base64,${imageBase64}`;
        profileImage.src = base64WithPrefix;
        console.log('✓ Image source replaced with base64 for PDF generation');
        
        // Wait for image to load
        await new Promise((resolve) => {
          if (profileImage.complete) {
            resolve();
          } else {
            profileImage.onload = resolve;
            profileImage.onerror = resolve;
          }
        });
      }

      // STEP 3: Hide button
      const buttonsContainer = idCardRef.current.querySelector('[data-exclude-from-capture]');
      let originalDisplay = '';
      if (buttonsContainer) {
        originalDisplay = buttonsContainer.style.display;
        buttonsContainer.style.display = 'none';
      }

      // STEP 3.5: Add space above card-header for PDF
      const cardHeader = idCardRef.current.querySelector('.card-header');
      let originalMarginTop = '';
      if (cardHeader) {
        originalMarginTop = cardHeader.style.marginTop;
        cardHeader.style.marginTop = '1cm';  // Add 1cm space at top for PDF
      }

      // Wait for any final rendering
      await new Promise(resolve => setTimeout(resolve, 500));

      // STEP 4: Capture with high quality for printing
      console.log('=== STEP 4: CAPTURING CANVAS ===');
      const canvas = await html2canvas(idCardRef.current, {
        scale: 3,  // High quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedButtons = clonedDoc.querySelector('[data-exclude-from-capture]');
          if (clonedButtons) clonedButtons.remove();

          const images = clonedDoc.querySelectorAll('img');
          images.forEach(img => {
            img.style.display = 'block';
            img.style.visibility = 'visible';
            img.style.opacity = '1';
          });

          const textElements = clonedDoc.querySelectorAll('h5, p, small');
          textElements.forEach(el => {
            el.style.visibility = 'visible';
          });
        }
      });

      console.log('✓ Canvas captured');
      console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);

      // STEP 5: Restore original image and button visibility
      if (originalImageSrc && profileImage) {
        profileImage.src = originalImageSrc;
        console.log('✓ Original image source restored');
      }

      if (buttonsContainer) {
        buttonsContainer.style.display = originalDisplay;
      }

      if (cardHeader) {
        cardHeader.style.marginTop = originalMarginTop;
      }

      // STEP 6: Generate PDF with 70mm x 100mm dimensions (exact card size)
      console.log('=== STEP 6: GENERATING PDF ===');
      const cardWidth = 100;   // ID card width: 70mm
      const cardHeight = 130;  // ID card height: 100mm

      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      // Create PDF with exact card dimensions (card fills entire page)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [cardWidth, cardHeight],
        compress: false
      });

      // Add the ID card image to fill entire PDF page (0, 0 to 70mm, 100mm)
      pdf.addImage(imgData, 'JPEG', 0, 0, cardWidth, cardHeight, undefined, 'SLOW');

      const userName = (displayUserData?.firstName || displayUserData?.name || 'User').replace(/[^a-zA-Z0-9]/g, '_');
      pdf.save(`ID_Card_${userName}_${Date.now()}.pdf`);

      console.log('✓✓✓ PDF GENERATED SUCCESSFULLY!');
      console.log(`ID Card size in PDF: ${cardWidth}mm × ${cardHeight}mm`);
      console.log('The ID card fills the entire PDF page - print at 100% scale');

    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to generate PDF: ${error.message}`);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  // Generate QR
  const generateQRData = async (userId) => {
    try {
      setQrLoading(true);
      const baseUrl = window.location.origin;
      const qrUrl = `${baseUrl}/userqr?id=${userId}`;
      setQrData(qrUrl);
      return qrUrl;
    } catch (error) {
      const fallbackUrl = `${window.location.origin}/userqr?id=${userId}`;
      setQrData(fallbackUrl);
      return fallbackUrl;
    } finally {
      setQrLoading(false);
    }
  };

useEffect(() => {
  console.log('=== AUTHENTICATION CHECK STARTED ===');
  
  const storedUser = localStorage.getItem('user');
  const token = localStorage.getItem('token') ||
    localStorage.getItem('Token') ||
    (storedUser ? JSON.parse(storedUser).token : null);

  console.log('📋 All localStorage items:');
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    const value = localStorage.getItem(key);
    console.log(`  ${key}:`, value?.substring(0, 50));
  }

  console.log('📋 Stored user:', storedUser);
  console.log('🔑 Token found:', !!token);
  if (token) {
    console.log('🔑 Token preview:', token.substring(0, 30) + '...');
  }

  if (!token) {
    console.log('❌ NO TOKEN FOUND - Redirecting to login');
    alert('NO TOKEN FOUND! Check console for localStorage contents.');
    // TEMPORARILY COMMENT OUT THE REDIRECT
    // navigate('/login');
    // return;
  }

  try {
    console.log('🔓 Attempting to decode JWT...');
    const decodedToken = decodeJWT(token);
    
    if (!decodedToken) {
      console.log('❌ INVALID TOKEN - Could not decode - Redirecting to login');
      alert('INVALID TOKEN! Check console.');
      // TEMPORARILY COMMENT OUT THE REDIRECT
      // navigate('/login');
      // return;
    }

    console.log('✅ Token decoded successfully:', decodedToken);

    const userFromToken = {
      id: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
        decodedToken.sub || decodedToken.userId,
      email: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
        decodedToken.email,
      name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
        decodedToken.name,
      exp: decodedToken.exp
    };

    console.log('👤 User from token:', userFromToken);

    // Check token expiration
    if (userFromToken.exp) {
      const currentTime = Date.now();
      const expirationTime = userFromToken.exp * 1000;
      const isExpired = currentTime >= expirationTime;
      
      console.log('⏰ Current time:', new Date(currentTime).toISOString());
      console.log('⏰ Token expires:', new Date(expirationTime).toISOString());
      console.log('⏰ Token expired:', isExpired);
      
      if (isExpired) {
        console.log('❌ TOKEN EXPIRED - Redirecting to login');
        // alert('TOKEN EXPIRED! Check console for dates.');
        // TEMPORARILY COMMENT OUT THE REDIRECT
        // localStorage.clear();
        // navigate('/login');
        // return;
      }
    }

    console.log('✅ Token is valid and not expired');
    setUserData(userFromToken);

    if (!userFromToken.id) {
      console.log('❌ No user ID found in token');
      setError('User ID not found');
      setLoading(false);
      return;
    }

    console.log('📡 Fetching user details for ID:', userFromToken.id);
    fetchUserDetails(userFromToken.id, token);
    generateQRData(userFromToken.id);

  } catch (parseError) {
    console.error('❌ ERROR PARSING TOKEN:', parseError);
    alert('ERROR PARSING TOKEN: ' + parseError.message);
    // TEMPORARILY COMMENT OUT THE REDIRECT
    // localStorage.clear();
    // navigate('/login');
  }
}, [navigate]);

  const fetchUserDetails = async (userId, token) => {
    try {
      setLoading(true);
      setError(null);

      console.log('=== FETCHING FROM MEMBERS API ===');
      console.log('User ID:', userId);

      const apiUrl = `https://api.asterickx.com/api/Members/${userId}`;
      console.log('API URL:', apiUrl);

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const userResponse = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
        mode: 'cors'
      });

      console.log('Response status:', userResponse.status);

      if (userResponse.ok) {
        const userDetailsData = await userResponse.json();

        console.log('=== MEMBERS API SUCCESS ===');
        console.log('Full Response:', userDetailsData);
        console.log('Photo path:', userDetailsData.photoPath);
        console.log('State:', userDetailsData.state);
        console.log('Phone:', userDetailsData.mobileNumber);

        setUserDetails(userDetailsData);
      } else if (userResponse.status === 401) {
        setError('Session expired. Redirecting...');
        localStorage.clear();
        setTimeout(() => navigate('/login'), 2000);
        return;
      } else {
        setError(`Failed: ${userResponse.status}`);
      }

      // Fetch tournaments from UAT API
      try {
        const [playerRegistrationsResponse, tournamentsResponse, teamsResponse] = await Promise.all([
          fetch('https://api.asterickx.com/api/PlayerRegistrations/', {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          }),
          fetch('https://api.asterickx.com/api/Tournament', {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          }),
          fetch('https://api.asterickx.com/api/Team', {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          })
        ]);

        let playerRegistrationsData = playerRegistrationsResponse.ok ? await playerRegistrationsResponse.json() : [];
        let allTournaments = tournamentsResponse.ok ? await tournamentsResponse.json() : [];
        let allTeams = teamsResponse.ok ? await teamsResponse.json() : [];

        const userRegistrations = Array.isArray(playerRegistrationsData)
          ? playerRegistrationsData.filter(reg => String(reg.playerId || reg.userId) === String(userId))
          : [];

        const tournamentMap = new Map();
        const teamMap = new Map();

        if (Array.isArray(allTournaments)) {
          allTournaments.forEach(t => {
            const id = t.id || t.tournamentId;
            if (id) {
              tournamentMap.set(String(id), t);
              tournamentMap.set(parseInt(id), t);
            }
          });
        }

        if (Array.isArray(allTeams)) {
          allTeams.forEach(t => {
            const id = t.id || t.teamId;
            if (id) {
              teamMap.set(String(id), t);
              teamMap.set(parseInt(id), t);
            }
          });
        }

        const tournamentData = userRegistrations.map(reg => {
          const tId = reg.tournamentId || reg.TournamentId;
          const teamId = reg.teamId || reg.TeamId;
          const tournament = tId ? (tournamentMap.get(String(tId)) || tournamentMap.get(parseInt(tId))) : null;
          const team = teamId ? (teamMap.get(String(teamId)) || teamMap.get(parseInt(teamId))) : null;

          return {
            id: reg.id,
            tournamentName: tournament?.name || `Tournament ${tId}` || 'Unknown',
            startDate: tournament?.startDate,
            endDate: tournament?.endDate,
            teamName: team?.name || `Team ${teamId}` || 'No Team',
            location: tournament?.location
          };
        });

        setTournaments(tournamentData);
      } catch (err) {
        console.error('Tournament error:', err);
      }

    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return 'Invalid Date'; }
  };

  const formatDayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric'
      });
    } catch { return 'Invalid Date'; }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border"></div>
          <p className="mt-2">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Error Loading Profile</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const displayUserData = userDetails || userData;
  const userPhotoUrl = getUserPhotoUrl(displayUserData);
  const userPhone = getUserPhone(displayUserData);
  const userState = getUserState(displayUserData);

  console.log('=== RENDERING ===');
  console.log('Photo URL:', userPhotoUrl);
  console.log('Phone:', userPhone);
  console.log('State:', userState);

  return (
    <div className="container mt-5" style={{ marginBottom: '80px' }}>
      <div className="row">
        <div className="col-md-3 mb-4">
          <div
            ref={idCardRef}
            className="card shadow h-100"
            style={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}></div>

            <div className="card-header text-center border-0" style={{ background: 'rgba(255, 255, 255, 0.14)', position: 'relative', zIndex: 1, color: 'white'}}>
              <h6 className="mb-0">Yonex-Sunrise 4th Mini National Badminton Championship-2025 Baroda</h6>
            </div>

            <div className="card-body text-center" style={{ position: 'relative', zIndex: 1, color: '#f0f0f0' }}>
              <div className="mb-3">
                <div className="rounded-circle bg-white d-inline-flex align-items-center justify-content-center overflow-hidden"
                  style={{ width: '120px', height: '120px', border: '4px solid white', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', position: 'relative' }}>
                  {userPhotoUrl ? (
                    <>
                      <img
                        src={userPhotoUrl}
                        alt="Profile"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0, display: 'block' }}
                        onLoad={(e) => {
                          console.log('✓✓✓ Photo loaded successfully');
                          e.target.style.opacity = '1';
                        }}
                        onError={(e) => {
                          console.error('✗✗✗ Photo failed to load');
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('.fa-user')) {
                            const icon = document.createElement('i');
                            icon.className = 'fas fa-user fa-3x text-primary';
                            icon.style.position = 'relative';
                            icon.style.zIndex = '1';
                            parent.appendChild(icon);
                          }
                        }}
                      />
                      <i className="fas fa-user fa-3x text-primary" style={{ position: 'relative', zIndex: 0, opacity: 0 }}></i>
                    </>
                  ) : (
                    <i className="fas fa-user fa-2x text-primary"></i>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <h5 className=" mb-1">
                  {displayUserData?.firstName && displayUserData?.lastName
                    ? `${displayUserData.firstName} ${displayUserData.lastName}`
                    : displayUserData?.name || 'N/A'}
                </h5>
                {userPhone && (
                  <p className="mb-1">
                    <i className="fas fa-phone me-2"></i>
                    <small className="fw-semibold">{userPhone}</small>
                  </p>
                )}
                {userState && (
                  <p className="mb-1">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    <small className="fw-semibold">{userState}</small>
                  </p>
                )}
              </div>

              <div className="mb-4">
                <div className="bg-white p-3 rounded d-inline-block">
                  {qrData ? (
                    <QRCode value={qrData} size={120} level="M" />
                  ) : (
                    <div style={{ width: '120px', height: '120px' }} className="d-flex align-items-center justify-content-center">
                      <div className="spinner-border text-primary"></div>
                    </div>
                  )}
                </div>
                <p className="mt-2 mb-0"><small className="opacity-75">Scan for details</small></p>
              </div>

              <div className="gap-2" data-exclude-from-capture="true">
                <button className="btn btn-primary fw-bold" onClick={downloadIDCard} disabled={downloading}>
                  {downloading ? (
                    <><div className="spinner-border spinner-border-sm me-2"></div>Downloading...</>
                  ) : (
                    <><i className="fas fa-download me-2"></i>Download ID Card</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-9 mb-4">
          <div className="card shadow">
            <div className="card-header text-white" style={{ backgroundColor: '#3069B5' }}>
              <h4 className="mb-0"><i className="fas fa-trophy me-2"></i>Tournament Registrations</h4>
            </div>
            <div className="card-body">
              {tournaments.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-info-circle fa-3x mb-3"></i>
                  <h5>No tournaments registered yet</h5>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Tournament Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Team Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tournaments.map((t, i) => (
                        <tr key={t.id || i}>
                          <td>{i + 1}</td>
                          <td>
                            <strong>{t.tournamentName}</strong>
                            {t.location && <small className="d-block text-muted">{t.location}</small>}
                          </td>
                          <td><span className="badge bg-info">{formatDate(t.startDate)}</span></td>
                          <td><span className="badge bg-info">{formatDate(t.endDate)}</span></td>
                          <td><span className="fw-bold text-primary">{t.teamName}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <div className="row">
        <div className="col-12 mb-4">
          <div className="card shadow">
            <div className="card-header text-white" style={{ backgroundColor: '#3069B5' }}>
              <h4 className="mb-0"><i className="fas fa-utensils me-2"></i>Meal Distribution</h4>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Day</th>
                      <th className="text-center"><i className="fas fa-coffee me-1"></i>Breakfast</th>
                      <th className="text-center"><i className="fas fa-hamburger me-1"></i>Lunch</th>
                      <th className="text-center"><i className="fas fa-mug-hot me-1"></i>Hi-Tea</th>
                      <th className="text-center"><i className="fas fa-moon me-1"></i>Dinner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealDistribution.map((day) => (
                      <tr key={day.id}>
                        <td><strong>{formatDayDate(day.date)}</strong></td>
                        <td className="text-center">
                          {day.breakfast ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {day.lunch ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {day.hiTea ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          {day.dinner ? <Check size={24} className="text-primary" /> : <span className="text-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Adminprofile;